import { FormApi } from "final-form";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "src/store";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import { Modal } from "src/ui/shared/Modal/Modal";
import { showErrorToast, showSuccessToast } from "src/ui/shared/toasts/Toasts";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";

import { serializeProduct, updateProductAndroidVersion, updateProductCodename } from "@dashboard/products/api";
import { updateProduct } from "@dashboard/products/store";
import { DialogProps, useTheme } from "@mui/material";
import { TextField } from "@shared/form/TextField";

const validationSchema = z.object({
    codename: z.string(),
    version: z.coerce.number(),
    name: z.string(),
});

const useStyles = makeStyles()((theme) => ({
    dialog: {
        minWidth: 400,
        gap: theme.spacing(1),
        display: "flex",
        flexDirection: "column",
    },
    errorMessage: {
        color: "red",
        fontSize: "12px",
        padding: theme.spacing(1),
    },
}));

type FormValues = {
    codename: string;
    version: string;
    duplicate: boolean;
    name: string;
};

type Props = DialogProps & {
    onClose: () => void;
    product: API.Product;
    products: API.Product[];
    setProducts: (products: API.Product[]) => void;
};

export const EditProductDialog = (props: Props) => {
    const dispatch = useDispatch();
    const { classes } = useStyles();
    const theme = useTheme();
    const { t } = useTranslation(["modals", "common"]);
    const codenameAndroidVersions = props.products
        .filter((item) => item.id !== props.product.id)
        .map((item) => item.codename + item.androidVersion);

    const onValidate = async (values: FormValues) => {
        const errors: Partial<Record<keyof FormValues, string>> = {};
        if (codenameAndroidVersions.includes(values.codename + values.version)) {
            const isDuplicate = props.products.some(
                (item) =>
                    item.id !== props.product.id && item.codename === values.codename && item.androidVersion === values.version,
            );

            if (isDuplicate) {
                errors.duplicate = t("modals:editProduct.productDuplicateError");
            }
        }

        const result = validationSchema.safeParse(values);
        if (!result.success) {
            result.error.issues.forEach((issue) => {
                if (issue.path.length > 0) {
                    const fieldName = issue.path[0] as keyof FormValues;
                    errors[fieldName] = issue.message;
                }
            });
        }

        return errors;
    };
    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        try {
            const updatedProduct = { ...props.product };

            if (form.getFieldState("version")?.dirty) {
                await updateProductAndroidVersion(props.product.id, values.version);
                updatedProduct.androidVersion = values.version;
            }
            if (form.getFieldState("codename")?.dirty) {
                await updateProductCodename(props.product.id, values.codename);
                updatedProduct.codename = values.codename;
            }
            if (form.getFieldState("name")?.dirty && updatedProduct.customBuildConfig) {
                await dispatch(updateProduct(serializeProduct([updatedProduct])[0]));
                updatedProduct.customBuildConfig.productName = values.name;
            }

            showSuccessToast(t("modals:editProduct.toast.productUpdatedSuccess"));
            const updatedProducts = props.products.map((product) =>
                product.id === updatedProduct.id ? updatedProduct : product,
            );

            props.setProducts(updatedProducts);
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("modals:editProduct.toast.productUpdatedError"));
        } finally {
            props.onClose();
        }
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={onValidate}
            initialValues={{
                codename: props.product.codename,
                version: props.product.androidVersion,
                name: props.product.customBuildConfig?.productName,
            }}
            render={({ handleSubmit, submitting, form, errors }) => (
                <form onSubmit={handleSubmit} id="editProduct">
                    <Modal
                        open={props.open}
                        onClose={props.onClose}
                        title={props.title}
                        endButton={
                            <PrimaryButton type="submit" disabled={submitting || form.getState().pristine} form="editProduct">
                                {t("common:button.save")}
                            </PrimaryButton>
                        }
                    >
                        <TextField
                            fullWidth
                            label={t("modals:editProduct.productName")}
                            name="name"
                            style={{ marginTop: theme.spacing(1) }}
                        />
                        <TextField fullWidth label={t("modals:editProduct.codename")} name="codename" />
                        <TextField fullWidth label={t("modals:editProduct.androidVersion")} name="version" />
                        {errors?.duplicate && <div className={classes.errorMessage}>{errors.duplicate}</div>}
                    </Modal>
                </form>
            )}
        />
    );
};
